import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Upload, ExternalLink, CheckCircle, Clock, Trash2, RefreshCw } from 'lucide-react';
import { api } from '../../context/AuthContext';

const STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  submitted: 'bg-blue-50  text-blue-700  dark:bg-blue-950/40  dark:text-blue-300',
  live:      'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  partial:   'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
};

export default function InfographicDistributionPage() {
  const [activeTab, setActiveTab] = useState('create');
  const [distributions, setDistributions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [infographics, setInfographics] = useState([]);
  const [selected, setSelected] = useState(['pinterest', 'visually', 'reddit_infographics', 'infogram']);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const [infographicId, setInfographicId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [tags, setTags] = useState('');

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const load = async () => {
    try {
      const [dists, plats] = await Promise.all([
        api.get('/infographic-distribution/'),
        api.get('/infographic-distribution/platforms'),
      ]);
      setDistributions(dists.data.distributions || []);
      setPlatforms(plats.data.platforms || []);
    } catch { /* silent */ }
    try {
      const { data } = await api.get('/images/infographics');
      setInfographics(data.infographics || data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { setStatus('Error: Select at least one platform.'); return; }
    setLoading(true);
    setStatus('Creating distribution...');
    try {
      await api.post('/infographic-distribution/', {
        infographic_id: infographicId,
        title,
        description,
        website_url: websiteUrl || null,
        tags: tags.split(',').map((k) => k.trim()).filter(Boolean),
        platforms: selected,
      });
      setStatus('Distribution created!');
      setInfographicId(''); setTitle(''); setDescription(''); setWebsiteUrl(''); setTags('');
      await load();
      setActiveTab('distributions');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const markStatus = async (id, platform, newStatus) => {
    try {
      await api.patch(`/infographic-distribution/${id}/platform/${platform}`, { status: newStatus });
      await load();
    } catch (err) { setStatus(`Error: ${errDetail(err)}`); }
  };

  const remove = async (id) => {
    try { await api.delete(`/infographic-distribution/${id}`); await load(); }
    catch (err) { setStatus(`Error: ${errDetail(err)}`); }
  };

  const tabs = [
    { id: 'create', label: 'Create' },
    { id: 'distributions', label: `Distributions (${distributions.length})` },
    { id: 'platforms', label: 'Platforms' },
  ];

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <BarChart3 className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Infographic Distribution</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Distribute rendered infographics across Pinterest, Visual.ly, Reddit, SlideShare and more.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Distributions', value: distributions.length },
          { label: 'Live', value: distributions.filter((s) => s.overall_status === 'live').length },
          { label: 'Partial', value: distributions.filter((s) => s.overall_status === 'partial').length },
          { label: 'Pending', value: distributions.filter((s) => s.overall_status === 'pending').length },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{s.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
            className={`pb-2 px-1 text-sm font-medium transition-colors ${activeTab === t.id ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium mb-1">Infographic <span className="text-red-500">*</span></label>
              {infographics.length > 0 ? (
                <select required value={infographicId} onChange={(e) => setInfographicId(e.target.value)}
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                  <option value="">Select infographic...</option>
                  {infographics.map((ig) => (
                    <option key={ig.id || ig.infographic_id} value={ig.id || ig.infographic_id}>
                      {ig.title || ig.id || ig.infographic_id}
                    </option>
                  ))}
                </select>
              ) : (
                <input type="text" required value={infographicId} onChange={(e) => setInfographicId(e.target.value)}
                  placeholder="Infographic ID"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Title <span className="text-red-500">*</span></label>
              <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description <span className="text-red-500">*</span></label>
              <textarea rows={3} required value={description} onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Website URL (for backlink)</label>
              <input type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tags <span className="text-xs text-slate-400">(comma separated)</span></label>
              <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Target Platforms <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {platforms.map((p) => (
                  <label key={p.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(p.id) ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} className="accent-violet-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{p.name}</p>
                      <p className="text-xs text-slate-400">{p.estimated_traffic} traffic</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {loading ? 'Creating...' : 'Create Distribution'}
            </button>
          </form>
        )}

        {activeTab === 'distributions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{distributions.length} total</p>
              <button type="button" onClick={load} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
            {distributions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No distributions yet.</p>
              </div>
            ) : distributions.map((sub) => (
              <div key={sub.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-sm">{sub.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{sub.description}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[sub.overall_status] || STATUS_STYLES.pending}`}>
                      {sub.overall_status}
                    </span>
                    <button type="button" onClick={() => remove(sub.id)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {sub.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {sub.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sub.platforms?.map((pt) => (
                    <div key={pt.platform} className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-md p-2">
                      <div>
                        <p className="text-xs font-medium">{pt.name}</p>
                        <span className={`text-xs ${STATUS_STYLES[pt.status] || STATUS_STYLES.pending} px-1.5 py-0.5 rounded capitalize`}>{pt.status}</span>
                      </div>
                      <div className="flex gap-1">
                        {pt.status === 'pending' && (
                          <button type="button" title="Mark Submitted" onClick={() => markStatus(sub.id, pt.platform, 'submitted')} className="text-blue-400 hover:text-blue-600">
                            <Clock className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {pt.status === 'submitted' && (
                          <button type="button" title="Mark Live" onClick={() => markStatus(sub.id, pt.platform, 'live')} className="text-green-400 hover:text-green-600">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {pt.submit_url && (
                          <a href={pt.submit_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-violet-500">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {sub.seo_benefits && (
                  <div className="flex gap-4 text-xs text-slate-500 pt-1 border-t border-slate-100 dark:border-slate-800">
                    <span>🔗 {sub.seo_benefits.backlinks_expected} backlinks</span>
                    <span>👁 ~{sub.seo_benefits.estimated_monthly_views.toLocaleString()} monthly views</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'platforms' && (
          <div className="grid md:grid-cols-2 gap-4">
            {platforms.map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-sm">{p.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{p.category}</p>
                  <div className="flex gap-3 mt-2 text-xs text-slate-500">
                    <span>Traffic: <strong className="text-slate-700 dark:text-slate-300">{p.estimated_traffic}</strong></span>
                    <span>DoFollow: <strong className="text-slate-700 dark:text-slate-300">{p.do_follow ? 'Yes' : 'No'}</strong></span>
                  </div>
                </div>
                {p.submit_url && (
                  <a href={p.submit_url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium shrink-0">
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
